import { useField } from 'vee-validate';
import { computed, toRefs } from 'vue';

import { type InteractionMode, modes } from '@/validation/interactionModes';

export interface FieldProps {
  name: string;
  label?: string;
  customId?: string;
  hint?: string;
  isDisabled?: boolean;
  required?: boolean;
  mode?: InteractionMode;
  // Props spécifiques pour FieldInput
  type?: string;
  maxLength?: number;
  // Props spécifiques pour FieldTextarea
  rows?: number;
}

export function useFieldValidation(props: FieldProps, defaultLabel: string = '') {
  const { name, label, customId, isDisabled, required, mode } = toRefs(props);

  const { value, errorMessage, handleChange, meta, handleBlur } = useField<string>(
    () => name.value,
    undefined,
    {
      validateOnValueUpdate: false,
    }
  );

  const handlers = computed(() => {
    const on: {
      [key: string]: (event: Event) => unknown
    } = {
      blur: handleBlur,
      input: (event: Event) => handleChange(event, false),
    };

    const triggers = modes[mode?.value ?? 'eager']({
      errorMessage,
      meta,
    });

    triggers.forEach((t) => {
      if (Array.isArray(on[t])) {
        on[t].push(handleChange);
      } else {
        on[t] = handleChange;
      }
    });

    return on;
  });

  const fieldId = computed(() => {
    return customId?.value ?? `${name.value}-input`;
  });

  const getFieldLabel = computed(() => {
    return label?.value ?? defaultLabel;
  });
  
  const computedLabel = computed(() => {
    return required?.value ? `${getFieldLabel.value} *` : getFieldLabel.value;
  });

  return {
    value,
    errorMessage,
    handleChange,
    handleBlur,
    meta,
    handlers,
    fieldId,
    getFieldLabel,
    computedLabel,
    isDisabled: computed(() => isDisabled?.value),
  };
}
